import { LCPhuketDistrict, LCSamuiDistrict } from '@/lib/entities/domain';
import {
  RentCatalogHru,
  RentCatalogQuery,
} from '@/lib/entities/rent/model/rent-catalog';

import { BASE_SERVICE_URL } from '@/lib/config';
import { skipToken, useQuery } from '@tanstack/react-query';

import { getCatalogFetchInit } from '../../catalog/lib/catalog-fetch-init';
import { getSuggestedLinks } from '../../catalog/lib/get-suggested-links';
import { rentKeys } from '../api/keys';
import { filterQuery } from '../lib/filter-query';
import { getSortedQueryParams } from '../lib/get-sorted-query-params';
import { isRentCatalogResponse } from '../lib/is-rent-catalog-response';
import { mapQuery } from '../lib/map-query';
import { RentCatalogResponse } from '../model/rent-catalog';
import { IShortRentData, RentLayout } from '../model/rent-page';

const BASE_RENT_URL = `${BASE_SERVICE_URL}/pages/thailand`;

export const fetchAllRentObjects = (): Promise<IShortRentData[]> => {
  return fetch(`${BASE_RENT_URL}/rent-objects`)
    .then(async (r) => {
      if (!r.ok) {
        return [];
      }
      const data: unknown = await r.json();
      return Array.isArray(data) ? (data as IShortRentData[]) : [];
    })
    .catch(() => []);
};
export const fetchRentLayout = async (
  slug: string,
  locale: string
): Promise<RentLayout | null> => {
  const url =
    `${BASE_RENT_URL}/rent-objects/by-slug?` +
    new URLSearchParams({
      lang: locale,
      slug: slug,
    }).toString();
  const response = await fetch(url);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch rent layout. Status: ${response.status}`);
  }
  return response.json();
};

export const fetchRentCatalog = (
  params: Partial<RentCatalogQuery>
): Promise<RentCatalogResponse | null> => {
  return fetch(
    `${BASE_RENT_URL}/rent-catalog?` +
      getSortedQueryParams<
        Partial<RentCatalogQuery>,
        [keyof RentCatalogQuery, RentCatalogQuery[keyof RentCatalogQuery]]
      >(params, filterQuery, mapQuery),
    getCatalogFetchInit()
  )
    .then(async (r) => {
      if (!r.ok) {
        return null;
      }
      const data: unknown = await r.json();
      return isRentCatalogResponse(data) ? data : null;
    })
    .catch(() => null);
};
const getDistrictParam = (
  samuiDistrict: LCSamuiDistrict[],
  phuketDistrict: LCPhuketDistrict[]
) => {
  const isAllSamui = samuiDistrict && samuiDistrict.includes('all');
  const isAllPhuket = phuketDistrict && phuketDistrict.includes('all');
  if (isAllSamui && isAllPhuket) {
    return { district: [] as (LCPhuketDistrict | LCSamuiDistrict)[] };
  }
  if (isAllSamui) {
    return { region: 'SAMUI', district: [] as LCSamuiDistrict[] };
  }
  if (isAllPhuket) {
    return { region: 'PHUKET', district: [] as LCPhuketDistrict[] };
  }
  return {
    district: [...samuiDistrict, ...phuketDistrict] as (
      | LCPhuketDistrict
      | LCSamuiDistrict
    )[],
  };
};

export const getRentQueryDefinition = (
  { samuiDistrict, phuketDistrict, ...params }: RentCatalogQuery,
  language: string
) => {
  const districtParam = getDistrictParam(samuiDistrict, phuketDistrict);
  const paramsWithLang = { ...params, ...districtParam, language };
  const queryKey = rentKeys.catalog(
    getSortedQueryParams(
      paramsWithLang as Partial<RentCatalogQuery>,
      filterQuery,
      mapQuery
    )
  );
  return {
    paramsWithLang,
    queryKey,
    queryFn: async () => {
      const data = await fetchRentCatalog(paramsWithLang);
      if (!data) {
        throw new Error('Rent catalog unavailable');
      }
      return data;
    },
    placeholderData: (prev: unknown) => prev,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  } as Parameters<typeof useQuery>[0];
};

export const useRentQuery = (
  { samuiDistrict, phuketDistrict, ...params }: RentCatalogQuery,
  language: string
) => {
  const districtParam = getDistrictParam(samuiDistrict, phuketDistrict);
  const paramsWithLang = { ...params, ...districtParam, language };
  const queryKey = rentKeys.catalog(
    getSortedQueryParams(
      paramsWithLang as Partial<RentCatalogQuery>,
      filterQuery,
      mapQuery
    )
  );
  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await fetchRentCatalog(paramsWithLang);
      if (!data) {
        throw new Error('Rent catalog unavailable');
      }
      return data;
    },
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useRentSearchQuery = (
  searchQuery: string,
  language: string = 'EN'
) => {
  return useQuery({
    queryKey: rentKeys.search(searchQuery, language),
    staleTime: 1000 * 60 * 5,
    retry: 1,
    queryFn: searchQuery
      ? async () => {
          const data = await fetchRentCatalog({ query: searchQuery, language });
          if (!data) {
            throw new Error('Rent catalog unavailable');
          }
          return data;
        }
      : skipToken,
  });
};

export const fetchRentCatalogLinks = (): Promise<RentCatalogHru> => {
  return fetch(`${BASE_RENT_URL}/rent-hru`)
    .then((r) => r.json())
    .then((data) => ({
      links: getSuggestedLinks(data.links),
      pinned: data.pinned || [],
    }))
    .catch(() => {
      return { links: [], pinned: [] };
    });
};

export const useRentSuggestQuery = () => {
  return useQuery({
    queryKey: rentKeys.suggest(),
    staleTime: 1000 * 60 * 60,
    queryFn: async () => fetchRentCatalogLinks(),
  });
};
